import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { UserRoles } from '../interface/users.interface';

export interface RoleOption {
  id: string;
  name: string;
}

/**
 * Orden fijo de presentación de roles en todos los selectores del dashboard.
 * Roles ausentes en este array se concatenan al final preservando el orden de entrada.
 */
const ROLE_PRIORITY: string[] = [
  UserRoles.admin,
  UserRoles.proprietor,
  UserRoles.tenant,
  UserRoles.committee,
  UserRoles.vigilant,
  UserRoles.customized,
  UserRoles.collaborator,
  UserRoles.external,
];

/** Set de UserRoles reconocidos: solo a estos se les aplica el filtro por white-label. */
const KNOWN_USER_ROLES: Set<string> = new Set(Object.values(UserRoles));

/**
 * Default Munily para actor `proprietor` cuando no hay
 * `assignableRolesByActor.proprietor` en white-label.
 */
export const MUNILY_PROPRIETOR_ASSIGNABLE_ROLES: readonly UserRoles[] = [
  UserRoles.proprietor,
  UserRoles.tenant,
  UserRoles.dependiente,
];

/**
 * Roles por defecto al crear reserva en macro si el área no define config/configByRol.
 */
export const MACRO_RESERVATION_DEFAULT_ROLES: readonly UserRoles[] = [
  UserRoles.tenant,
  UserRoles.dependiente,
  UserRoles.proprietor,
  UserRoles.admin,
  UserRoles.customized,
  UserRoles.collaborator,
  UserRoles.external,
];

/**
 * Centraliza filtrado y orden de roles para selectores del dashboard
 * (filtros, creación y edición de usuarios).
 *
 * Capas (intersección):
 * 1. `roleLabels` — si existe, solo roles con key en ese mapa.
 * 2. Roles asignables por actor:
 *    - `admin`: solo si WL define `assignableRolesByActor.admin`.
 *    - `proprietor` (solo): WL `assignableRolesByActor.proprietor` si existe;
 *      si no, default Munily `proprietor|tenant|dependiente`.
 * 3. Orden siempre según `ROLE_PRIORITY`.
 *
 * El servicio no resuelve nombres traducidos: cada consumidor pasa un
 * `labelFor` (típicamente `RoleLabelPipe.transform`).
 */
@Injectable({ providedIn: 'root' })
export class RoleOptionsService {
  constructor(private dataService: DataService) {}

  /**
   * Aplica el filtro por white-label y el orden fijo a una lista de roles
   * candidatos. Devuelve solo los IDs (sin nombres traducidos).
   */
  filterAndSort(candidateRoles: string[]): string[] {
    const unique = Array.from(new Set(candidateRoles ?? []));
    const allowed = this.getAllowedRoles();
    let filtered = allowed ? unique.filter((r) => this.passesWhiteLabel(r, allowed)) : unique;
    const actorAllowed = this.getAssignableRolesByActorWhitelist();
    if (actorAllowed) {
      filtered = filtered.filter((r) => actorAllowed.has(r));
    }
    return this.sortByPriority(filtered, (r) => r);
  }

  /**
   * Filtra/ordena una lista de objetos arbitrarios extrayendo el rol con
   * `getRoleKey`. Útil para listas como `{ value, display }` o `{ id, name }`
   * que no se quieren reconstruir.
   */
  filterAndSortItems<T>(items: T[], getRoleKey: (item: T) => string): T[] {
    if (!items?.length) return [];
    const allowed = this.getAllowedRoles();
    let filtered = allowed ? items.filter((item) => this.passesWhiteLabel(getRoleKey(item), allowed)) : items.slice();
    const actorAllowed = this.getAssignableRolesByActorWhitelist();
    if (actorAllowed) {
      filtered = filtered.filter((item) => actorAllowed.has(getRoleKey(item)));
    }
    return this.sortByPriority(filtered, getRoleKey);
  }

  /**
   * Construye opciones `{ id, name }` para un selector. El `labelFor` recibe el
   * id del rol y devuelve el nombre a mostrar (normalmente `roleLabelPipe.transform`).
   */
  buildOptions(candidateRoles: string[], labelFor: (id: string) => string): RoleOption[] {
    return this.filterAndSort(candidateRoles).map((id) => ({ id, name: labelFor(id) }));
  }

  /** Opciones `{ value, display }` para ng-select a partir de `MACRO_RESERVATION_DEFAULT_ROLES`. */
  buildMacroReservationDefaultRoleSelectItems(): { value: string; display: string }[] {
    return MACRO_RESERVATION_DEFAULT_ROLES.map((value) => ({
      value,
      display: `rols.${value}`,
    }));
  }

  /**
   * Devuelve el set de roles permitidos por white-label, o `null` cuando el
   * building no tiene `roleLabels` (en cuyo caso se respeta el fallback Munily).
   */
  getAllowedRoles(): Set<string> | null {
    const labels = this.getRoleLabelsConfig();
    if (!labels) return null;
    const keys = Object.keys(labels);
    if (!keys.length) return null;
    return new Set(keys);
  }

  /**
   * Whitelist de roles asignables para el actor actual.
   * - Admin: solo si existe `assignableRolesByActor.admin`; si no → null.
   * - Proprietor-only: WL `proprietor` si existe; si no → default Munily.
   * No usa `whiteLabel.type` (deprecado).
   */
  getAssignableRolesByActorWhitelist(): Set<string> | null {
    if (this.dataService.isBuildingAdmin()) {
      const config = this.getAssignableRolesByActorConfig();
      if (!config || !Array.isArray(config.admin)) return null;
      return new Set(config.admin.filter((r: unknown) => typeof r === 'string'));
    }
    if (this.dataService.isProprietorOnly()) {
      const config = this.getAssignableRolesByActorConfig();
      if (config && Array.isArray(config.proprietor)) {
        return new Set(config.proprietor.filter((r: unknown) => typeof r === 'string'));
      }
      return new Set(MUNILY_PROPRIETOR_ASSIGNABLE_ROLES);
    }
    return null;
  }

  /** @deprecated Use getAssignableRolesByActorWhitelist */
  getMtsAssignableRoles(): Set<string> | null {
    return this.getAssignableRolesByActorWhitelist();
  }

  /** True si el actor actual tiene whitelist de roles asignables (WL o default Munily). */
  hasAssignableRolesByActorRestriction(): boolean {
    return this.getAssignableRolesByActorWhitelist() != null;
  }

  /** @deprecated Use hasAssignableRolesByActorRestriction — type=mts no es el gate. */
  isMtsBuilding(): boolean {
    return this.hasAssignableRolesByActorRestriction();
  }

  /**
   * True si el rol pasa `roleLabels` (si hay) y la whitelist del actor (si hay).
   */
  isRoleAllowed(role: string): boolean {
    const allowed = this.getAllowedRoles();
    if (allowed && !this.passesWhiteLabel(role, allowed)) {
      return false;
    }
    const actorAllowed = this.getAssignableRolesByActorWhitelist();
    if (actorAllowed && KNOWN_USER_ROLES.has(role) && !actorAllowed.has(role)) {
      return false;
    }
    return true;
  }

  /**
   * Rol sugerido al crear/editar con whitelist de actor: preferir `tenant` si está
   * permitido; si no, el primero de la lista.
   */
  getPreferredAssignableRole(): string | null {
    const whitelist = this.getAssignableRolesByActorWhitelist();
    if (!whitelist || whitelist.size === 0) return null;
    if (whitelist.has(UserRoles.tenant)) return UserRoles.tenant;
    return [...whitelist][0] ?? null;
  }

  /**
   * Filtro por white-label aplicado solo a roles conocidos del enum `UserRoles`.
   * Cualquier valor desconocido (e.g. filtros sintéticos como `usergroup`)
   * pasa sin ser filtrado.
   */
  private passesWhiteLabel(role: string, allowed: Set<string>): boolean {
    if (!KNOWN_USER_ROLES.has(role)) return true;
    return allowed.has(role);
  }

  private sortByPriority<T>(items: T[], getRoleKey: (item: T) => string): T[] {
    return [...items].sort((a, b) => {
      const ai = ROLE_PRIORITY.indexOf(getRoleKey(a));
      const bi = ROLE_PRIORITY.indexOf(getRoleKey(b));
      const aRank = ai === -1 ? Number.MAX_SAFE_INTEGER : ai;
      const bRank = bi === -1 ? Number.MAX_SAFE_INTEGER : bi;
      return aRank - bRank;
    });
  }

  private getRoleLabelsConfig(): Record<string, unknown> | undefined {
    const building = this.dataService.getBuilding();
    return building?.config?.whiteLabel?.projects?.dashboard?.roleLabels;
  }

  private getAssignableRolesByActorConfig():
    | { admin?: unknown; proprietor?: unknown }
    | undefined {
    const building = this.dataService.getBuilding();
    const config =
      building?.config?.whiteLabel?.projects?.dashboard?.assignableRolesByActor;
    if (!config || typeof config !== 'object') return undefined;
    return config;
  }
}
